/**
 * Background scenes.
 *
 * A scene paints the surface *behind* everything and nothing else: no text
 * colours, no tile colours, no panel colours. That is what keeps it safe to
 * offer freely — every scene is composed out of the current theme's own
 * `--bg-0` and `--text-0`, mixed a few percent, so each one works in light
 * mode and dark mode without a second definition and without ever putting
 * pale text on a pale field.
 *
 * Menus and the game screen are chosen separately, because they are looked at
 * differently. A menu is read for ten seconds and can afford some character; a
 * board is stared at for twenty minutes with a hundred letters on it, and
 * anything with real contrast behind those letters becomes exhausting. The
 * descriptions say so, rather than leaving somebody to find out.
 */
export type SceneId =
  | 'plain' | 'halo' | 'library' | 'dusk' | 'linen' | 'aurora' | 'blueprint'
  // Rooms — painted, for behind the menus only.
  | 'fireside' | 'study' | 'baywindow' | 'candlelight'
  // Table surfaces — for the board only.
  | 'felt' | 'walnut' | 'slate' | 'leather';

export interface Scene {
  readonly id: SceneId;
  readonly name: string;
  readonly note: string;
  /** True for the ones quiet enough to sit behind a full board of letters. */
  readonly restful: boolean;
  /** Where it may be used: an illustrated room is no surface to build words on. */
  readonly for: 'both' | 'menu' | 'board';
}

export const SCENES: readonly Scene[] = [
  { id: 'plain', name: 'Plain', restful: true, for: 'both',
    note: 'Flat colour. Nothing behind the letters at all.' },
  { id: 'halo', name: 'Halo', restful: true, for: 'both',
    note: 'A soft lift in the middle, falling away at the edges. Draws the eye ' +
          'to the centre without anybody noticing why.' },
  { id: 'library', name: 'Library', restful: true, for: 'both',
    note: 'A warm lamp from above and the faintest ruled lines. Meant to feel ' +
          'like a table in a reading room.' },
  { id: 'dusk', name: 'Dusk', restful: true, for: 'both',
    note: 'Indigo settling into plum, top to bottom. The most colour of the ' +
          'restful ones.' },
  { id: 'linen', name: 'Linen', restful: true, for: 'both',
    note: 'A woven grain, two per cent of a shade deep. You will see it on a ' +
          'good screen and not on a phone in sunlight.' },
  { id: 'aurora', name: 'Aurora', restful: false, for: 'both',
    note: 'Slow blooms of teal, violet and amber. Handsome on a menu; busy ' +
          'behind a hundred letters.' },
  { id: 'blueprint', name: 'Blueprint', restful: false, for: 'both',
    note: 'A cool draughtsman’s grid. It reads well on a menu, and on the ' +
          'board it fights the squares that are already there.' },

  // ---- rooms: a games table in a living room, behind the menus ----------
  // Fireside uses generated portrait/landscape photography (docs/room-art.md).
  // The other room options are painted by tools/paint-rooms.mjs.
  { id: 'fireside', name: 'Fireside', restful: true, for: 'menu',
    note: 'Warm afternoon light, a small fire, and a games table ready to play.' },
  { id: 'study', name: 'Study', restful: true, for: 'menu',
    note: 'Shelves of books behind the table and a reading lamp at either end.' },
  { id: 'baywindow', name: 'Bay window', restful: true, for: 'menu',
    note: 'A city at dusk beyond the glass, curtains drawn back, and a lamp ' +
          'lit against the evening.' },
  { id: 'candlelight', name: 'Candlelight', restful: true, for: 'menu',
    note: 'Candles on the table, their light in the polish, and a string of ' +
          'little lights across the dark.' },

  // ---- surfaces: what the board itself is made of ----------------------
  { id: 'felt', name: 'Green felt', restful: true, for: 'board',
    note: 'A card table\u2019s baize. The classic surface for tiles.' },
  { id: 'walnut', name: 'Walnut', restful: true, for: 'board',
    note: 'A dark polished wood grain, like a proper games table.' },
  { id: 'slate', name: 'Slate', restful: true, for: 'board',
    note: 'Cool blue-grey stone. Quiet, and the letters stand right out.' },
  { id: 'leather', name: 'Leather', restful: true, for: 'board',
    note: 'An oxblood desk-top with a faint tooled border.' },
];

export const sceneById = (id: string): Scene =>
  SCENES.find((s) => s.id === id) ?? SCENES[0]!;

/** Whether a remembered choice still exists and suits where it is used. */
export const sceneFits = (id: string, where: 'menu' | 'board'): boolean =>
  SCENES.some((s) => s.id === id && (s.for === 'both' || s.for === where));

export const DEFAULT_SCENE_MENU: SceneId = 'fireside';
export const DEFAULT_SCENE_GAME: SceneId = 'halo';

/** How strongly the board's squares are drawn. */
export type GridStrength = 'off' | 'faint' | 'normal' | 'strong';
export const GRID_STRENGTHS: ReadonlyArray<{ id: GridStrength; name: string }> = [
  { id: 'off', name: 'No grid' },
  { id: 'faint', name: 'Faint' },
  { id: 'normal', name: 'Normal' },
  { id: 'strong', name: 'Clear' },
];
